import { useTranslation } from 'react-i18next';
import { Select } from '@/components/ui';
import type { RoleMapTableVM } from './useRoleMapTable';

/**
 * EAA-1837: the footer row of `role-map-table.html` — "Đã chọn N / M dòng", a
 * page-size selector, "Trang X / Y" and the « ‹ n › » pager. Same client-side
 * paging model and same first/last + ±2 page window as
 * `accounts/AccountsPagination.tsx`, so the app's two bulk-select tables page
 * identically; only the chrome follows this screen's export.
 */
export function RoleMapPagination({ vm }: { vm: RoleMapTableVM }) {
    const { t } = useTranslation();
    const pages = new Set<number>([
        1,
        vm.totalPages,
        vm.page - 2,
        vm.page - 1,
        vm.page,
        vm.page + 1,
        vm.page + 2,
    ]);
    const pageList = [...pages].filter((p) => p >= 1 && p <= vm.totalPages).sort((a, b) => a - b);

    return (
        <div className="rm-footer">
            <span className="rm-footer__selection">
                {t('console.roleMap.pagination.selectionText', {
                    selected: vm.selectedCount,
                    total: vm.filteredCount,
                })}
            </span>
            <span className="rm-footer__spacer" />
            {/* EAA-1817: raw <select> → kit Select, same value/options. */}
            <label className="rm-footer__page-size">
                {t('console.roleMap.pagination.pageSizeLabel')}
                <Select
                    value={vm.pageSize}
                    onChange={(value) => vm.setPageSize(Number(value))}
                    options={vm.pageSizeOptions.map((size) => ({ value: size, label: size }))}
                />
            </label>
            <span className="rm-footer__page">
                {t('console.roleMap.pagination.pageLabel', {
                    page: vm.page,
                    totalPages: vm.totalPages,
                })}
            </span>
            <div className="rm-pager">
                <button
                    type="button"
                    disabled={vm.page <= 1}
                    onClick={() => vm.setPage(1)}
                    aria-label={t('console.roleMap.pagination.firstPageAria')}
                >
                    «
                </button>
                <button
                    type="button"
                    disabled={vm.page <= 1}
                    onClick={() => vm.setPage(vm.page - 1)}
                    aria-label={t('console.roleMap.pagination.prevPageAria')}
                >
                    ‹
                </button>
                {pageList.map((p) => (
                    <button
                        key={p}
                        type="button"
                        className={p === vm.page ? 'is-current' : undefined}
                        onClick={() => vm.setPage(p)}
                        aria-current={p === vm.page ? 'page' : undefined}
                    >
                        {p}
                    </button>
                ))}
                <button
                    type="button"
                    disabled={vm.page >= vm.totalPages}
                    onClick={() => vm.setPage(vm.page + 1)}
                    aria-label={t('console.roleMap.pagination.nextPageAria')}
                >
                    ›
                </button>
                <button
                    type="button"
                    disabled={vm.page >= vm.totalPages}
                    onClick={() => vm.setPage(vm.totalPages)}
                    aria-label={t('console.roleMap.pagination.lastPageAria')}
                >
                    »
                </button>
            </div>
        </div>
    );
}
